/**
* AccesoriosPage component
* @returns <AccesoriosPage />
*/

// Import required components and hooks
import TitleAndButton from "../components/TitleAndButton";
import Breadcrumbs from "../components/Breadcrumbs";
import StoreGridContainer from "../components/StoreGridContainer";
import FilterContainer from "../components/FilterContainer";
import FiltersContainer from "../components/FiltersContainer";
import BoxWithBackgroundContainer from "../components/BoxWithBackgroundContainer";
import InputRadio from "../components/InputRadio";
import InputCheckBox from "../components/InputCheckBox";
import Accordion from "../components/Accordion";
import Drawer from "../components/Drawer";
import Button from "../components/Button";
import Loader from "../components/Loader";
import LegalFooter from "../components/LegalFooter";

import { useEffect, useState } from "react";
import { useGetAllAccesoriesQuery, useGetLegalesQuery } from "../redux/apiSlice";
import { useSearchParams } from "react-router-dom";
import { uniqueModelsArray, uniqueVersionsArray, uniqueTypesArray } from "../scripts/helpers/uniqueReducers";
import { AnimatePresence } from "framer-motion";

// AccesoriosPage component definition
const AccesoriosPage = () => {
    // Get search params from URL
    const [searchParams, setSearchParams] = useSearchParams();

    // Fetch all accessories using the useGetAllAccesoriesQuery hook
    const {
        data: accessories,
        isLoading: accessoriesIsLoading,
        isFetching: accessoriesIsFetching,
        isSuccess: accessoriesIsSuccess,
        isError: accessoriesIsError
    } = useGetAllAccesoriesQuery();

    // Fetch all legals using the useGetLegalesQuery hook
    const { 
        data: legals, 
        isLoading: legalsIsLoading, 
        isFetching: legalsIsFetching, 
        isSuccess: legalsIsSuccess, 
        isError: legalsIsError 
    } = useGetLegalesQuery();

    // State - initialize from URL params
    const [selectedType, setSelectedType] = useState(searchParams.get("tipo") || "all");
    const [selectedModel, setSelectedModel] = useState(searchParams.get("serie") || "all");
    const [selectedVersion, setSelectedVersion] = useState(searchParams.get("version") || "all");
    const [showOnlineDiscount, setShowOnlineDiscount] = useState(false);
    const [hasOnlineDiscountItems, setHasOnlineDiscountItems] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    useEffect(() => {
        if (!accessoriesIsSuccess || !accessories) {
            setHasOnlineDiscountItems(false);
            return;
        }

        const hasDiscountItems = accessories.some((item) => {
            return (
                item.price_discount !== null &&
                item.price_discount !== undefined &&
                item.price_discount !== ""
            );
        });

        setHasOnlineDiscountItems(hasDiscountItems);

        if (!hasDiscountItems) {
            setShowOnlineDiscount(false);
        }
    }, [accessoriesIsSuccess, accessories]);

    // console.log(accessories)

    // Filters
    const filterByType = (type, items) => {
        // console.log(`filterByType ${type}`)
        if (type === "all") {
            return items;
        } else {
            const itemsFilteredByType = items.filter((item) => {
                // console.log("item", item)
                for (const segment of item.segments) {
                    // console.log("segment", segment.name.toLowerCase())
                    // console.log(segment.name, selectedType)
                    if (segment.name === selectedType) {
                        // console.log(segment.name, selectedType)
                        // console.log("match", item)
                        return item
                    }
                }
            });

            // console.log(itemsFilteredByType)
            return itemsFilteredByType;
        }
    }

    const filterByModel = (model, items) => {
        // console.log(`filterByType ${type}`)
        if (model === "all") {
            return items;
        } else {
            const itemsFilteredByModel = items.filter((item) => {
                // console.log("item", item)
                for (const serie of item.series) {
                    // console.log("serie", item.series)
                    // console.log("serie name", serie.name)
                    // console.log(selectedModel)
                    if (serie.name === selectedModel) {
                        // console.log("match", item)
                        return item
                    }
                }
            });

            return itemsFilteredByModel;
        }
    }

    const filterByVersion = (version, items) => {
        // console.log(`filterByType ${type}`)
        if (version === "all") {
            return items;
        } else {
            const itemsFilteredByVersion = items.filter((item) => {
                // console.log("item", item)
                for (const version of item.versions) {
                    // console.log("serie", item.series)
                    // console.log("serie name", serie.name)
                    // console.log(selectedModel)
                    if (version.name === selectedVersion) {
                        // console.log("match", item)
                        return item
                    }
                }
            });
            return itemsFilteredByVersion;
        }
    }

    const filterByOnlineDiscount = (showDiscount, items) => {
        if (!showDiscount) {
            return items;
        }

        return items.filter((item) => {
            return (
                item.price_discount !== null &&
                item.price_discount !== undefined &&
                item.price_discount !== ""
            );
        });
    };

    // Event handlers for updating selected type, model, and version
    // Set the selected type to the value of the input. Ej: suv
    const typeChangeHandler = (e) => {
        const type = e.target.value;

        setSelectedType(type);

        // Reset selected model
        setSelectedModel(() => "all");

        // Reset selected version
        setSelectedVersion(() => "all");

        // Update URL
        const newParams = new URLSearchParams(searchParams);
        if (type && type !== "all") {
            newParams.set("tipo", type);
        } else {
            newParams.delete("tipo");
        }
        newParams.delete("serie");
        newParams.delete("version");
        setSearchParams(newParams);
    }

    // Set the selected model or serie to the value of the input. Ej: UX
    const modelChangeHandler = (e) => {
        const model = e.target.value;
        // console.log(model)

        setSelectedModel(model);

        // Reset selected version
        setSelectedVersion(() => "all");

        // Update URL
        const newParams = new URLSearchParams(searchParams);
        if (model && model !== "all") {
            newParams.set("serie", model);
        } else {
            newParams.delete("serie");
        }
        newParams.delete("version");
        setSearchParams(newParams);
    }

    // Set the selected version to the value of the input. Ej: UX200
    const versionChangeHandler = (e) => {
        const version = e.target.value;
        // console.log(version)

        setSelectedVersion(version);

        // Update URL
        const newParams = new URLSearchParams(searchParams);
        if (version && version !== "all") {
            newParams.set("version", version);
        } else {
            newParams.delete("version");
        }
        setSearchParams(newParams);
    };

    const onlineDiscountChangeHandler = (e) => {
        setShowOnlineDiscount(e.target.checked);
    };

    // Function to toggle the drawer
    const toggleDrawer = () => {
        setIsDrawerOpen(!isDrawerOpen);
    };

    // Items filtered
    let filteredItems;

    // Type list
    let typeList;

    // Model List
    let modelList;

    // Version list
    let versionList;

    // Prepare filtered items, type list, model list, and version list if accessories data is successfully fetched
    if (accessoriesIsSuccess) {
        filteredItems = filterByType(selectedType, accessories);
        filteredItems = filterByModel(selectedModel, filteredItems);
        filteredItems = filterByVersion(selectedVersion, filteredItems);
        filteredItems = filterByOnlineDiscount(showOnlineDiscount, filteredItems);

        typeList = uniqueTypesArray(selectedType, accessories);
        // console.log("typeList", typeList);
        modelList = uniqueModelsArray(selectedType, accessories);
        // console.log("modelList", modelList);
        versionList = uniqueVersionsArray(selectedModel, accessories);
        // console.log("versionList", versionList);
    }

    const BreadcrumbsProps = {
        links: [
            {
                link: "/",
                label: "Inicio"
            },
            {
                link: location.pathname,
                label: location.pathname.slice(1)
            },
        ],
    }

    const TitleAndButtonProps = {
        title: "Conoce todos los <br> accesorios disponibles",
        buttons: [
            // {
            //     config: {
            //         tag: "button",
            //         text: "Encuentra tu Lexus",
            //         color: "black",
            //     },
            // }
        ],
        backgroundColor: "white",
        extraClass: ""
    }

    // Prepare data for filter options
    const data = {
        image: "",
        title: "¿Para qué modelo estás buscando?",
        filters: {
            type: {
                title: "Tipo",
                options: [
                    {
                        type: "radio",
                        id: "type-all",
                        label: "Todos",
                        name: "type-all",
                        value: "all",
                        isRequired: false,
                    },
                ]
            },
            model: {
                title: "Serie",
                options: [
                    {
                        type: "radio",
                        id: "model-all",
                        label: "Todos",
                        name: "model-all",
                        value: "all",
                        isRequired: false,
                    }
                ]
            },
            version: {
                title: "Versión",
                options: [
                    {
                        type: "radio",
                        id: "version-all",
                        label: "Todas",
                        name: "version-all",
                        value: "all",
                        isRequired: false,
                    }
                ]
            }
        }
    }

    // Create input lists for types, models, and versions
    const typeInputList = data.filters.type.options.map((option, i) => {
        return (
            <InputRadio {...option} key={i} isChecked={selectedType === option.value} changed={typeChangeHandler} />
        )
    })

    const typeOptionsList = typeList?.map((item, i) => {
        return (
            <InputRadio
                type="radio"
                id={`type-${item}`}
                label={item.toUpperCase()}
                name={`type-${item}`}
                value={item}
                isRequired={false}
                key={i}
                isChecked={selectedType === item}
                changed={typeChangeHandler}
            />
        )
    })

    const modelInputList = data.filters.model.options.map((option, i) => {
        return (
            <InputRadio {...option} key={i} isChecked={selectedModel === option.value} changed={modelChangeHandler} />
        )
    })

    const modelOptionsList = modelList?.map((item, i) => {
        return (
            <InputRadio
                type="radio"
                id={`model-${item}`}
                label={item.toUpperCase()}
                name={`model-${item}`}
                value={item}
                isRequired={false}
                key={i}
                isChecked={selectedModel === item}
                changed={modelChangeHandler}
            />
        )
    })

    const versionInputList = data.filters.version.options.map((option, i) => {
        return (
            <InputRadio {...option} key={i} changed={versionChangeHandler} isChecked={selectedVersion === option.value} />
        )
    })

    const versionOptionList = versionList?.map((item, i) => {
        return (
            <InputRadio
                type="radio"
                id={`version-${item}`}
                label={item.toUpperCase()}
                name={`version-${item}`}
                value={item}
                isRequired={false}
                key={i}
                changed={versionChangeHandler}
                isChecked={selectedVersion === item}
            />
        )
    })

    // Prepare drawer filters and content
    const drawerFilters = (
        [
            {
                label: "Tipo",
                content: (
                    <div>
                        {typeInputList}

                        {typeList && typeOptionsList}
                    </div>
                )
            },
            {
                label: "Modelo",
                content: (
                    <div>
                        {modelInputList}

                        {modelList && modelOptionsList}
                    </div>
                )
            },
            {
                label: "Versión",

                content: (
                    <div>
                        {versionInputList}

                        {versionList && versionOptionList}
                    </div>
                )
            },
            ...(hasOnlineDiscountItems
                ? [
                    {
                        label: "Descuento",
                        content: (
                            <InputCheckBox
                                type="checkbox"
                                id="online-discount-mobile"
                                name="online-discount-mobile"
                                label="Descuento Online"
                                value="online-discount-mobile"
                                isChecked={showOnlineDiscount}
                                changed={onlineDiscountChangeHandler}
                            />
                        )
                    }
                ]
                : [])
        ]
    )

    const drawerContent = (
        <>
            <Accordion items={drawerFilters} type={"filters"} />
        </>
    );

    return (
        <>
            {accessoriesIsLoading && (
                <AnimatePresence>
                    <Loader />
                </AnimatePresence>
            )}

            {accessoriesIsSuccess && (
                <>
                    <Breadcrumbs {...BreadcrumbsProps} />

                    <TitleAndButton {...TitleAndButtonProps} />

                    <StoreGridContainer>
                        <Button tag="link" text="Filtrar por" hasIcon={"true"} onClick={toggleDrawer} />

                        <FiltersContainer>
                            <h3>{data.title}</h3>

                            <form action="">
                                {/* Type, model and version */}
                                <fieldset className="fieldset-1">
                                    {/* Type */}
                                    <FilterContainer title={data.filters.type.title}>
                                        {typeInputList}

                                        {typeList && typeOptionsList}
                                    </FilterContainer>

                                    {/* Model */}
                                    <FilterContainer title={data.filters.model.title}>
                                        {modelInputList}

                                        {modelList && modelOptionsList}
                                    </FilterContainer>

                                    {/* Version */}
                                    <FilterContainer title={data.filters.version.title}>
                                        {versionInputList}

                                        {versionList && versionOptionList}
                                    </FilterContainer>
                                </fieldset>

                                {hasOnlineDiscountItems && (
                                    <fieldset className="fieldset-2">
                                        <FilterContainer title={"Descuento"}>
                                            <InputCheckBox
                                                type="checkbox"
                                                id="online-discount"
                                                name="online-discount"
                                                label="Descuento Online"
                                                value="online-discount"
                                                isChecked={showOnlineDiscount}
                                                changed={onlineDiscountChangeHandler}
                                            />
                                        </FilterContainer>
                                    </fieldset>
                                )}
                            </form>
                        </FiltersContainer>

                        <BoxWithBackgroundContainer items={filteredItems} extraClass="--store-version" />

                        <Drawer title={"¿Qué modelo estás buscando?"} content={drawerContent} isOpen={isDrawerOpen} close={toggleDrawer} />
                    </StoreGridContainer>
                </>
            )}

            {accessoriesIsError && <p>Hubo un error</p>}

            {legalsIsSuccess && <LegalFooter content={legals.accessories}/>}
        </>
    )
}
export default AccesoriosPage
